import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import {
  DatabaseOutlined,
  BarChartOutlined,
  SafetyOutlined,
  FileTextOutlined,
  EyeOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import "./NSDL.css";

const NSDLHome = () => {
  const history = useNavigate();
  const { isDark } = useContext(ThemeContext);

  const nsdlServices = [
    {
      id: 1,
      title: "Corporate Bonds",
      subtitle: "Corporate Inventory",
      description:
        "Comprehensive database of corporate bond instruments with detailed issuer information, maturity profiles, and yield data. Access 38,965+ instruments with real-time pricing and risk assessment.",
      icon: <BarChartOutlined />,
      color: "#667eea",
      route: "/d/nsdl/1",
      features: ["ISIN Tracking", "Issuer Analysis", "Maturity Profiles"],
      stats: {
        total: "38,965",
        active: "35,200+",
        volume: "₹2,45,000 Cr",
      },
    },
    {
      id: 2,
      title: "Commercial Papers",
      subtitle: "Commercial Papers Inventory",
      description:
        "Short-term debt instruments issued by corporations with detailed maturity schedules, credit ratings, and issuer information. Track commercial papers with comprehensive analytics.",
      icon: <FileTextOutlined />,
      color: "#4ecdc4",
      route: "/d/nsdl/2",
      features: ["Short-term Instruments", "Credit Ratings", "Issuer Details"],
      stats: {
        total: "1,250+",
        active: "980+",
        volume: "₹18,500 Cr",
      },
    },
    {
      id: 3,
      title: "Preference Shares",
      subtitle: "Active Preference Shares",
      description:
        "Equity instruments with preferential rights, providing detailed dividend information and voting rights.",
      icon: <RiseOutlined />,
      color: "#ff6b6b",
      route: "/d/nsdl/3",
      features: ["Dividend tracking", "Voting rights", "Preference analysis"],
      stats: {
        total: "850+",
        active: "720+",
        volume: "₹12,800 Cr",
      },
    },
    {
      id: 4,
      title: "Defaulted Instruments",
      subtitle: "Defaulted in Redemption",
      description:
        "Instruments where issuers have defaulted in redemption payments. Currently tracking 2 defaulted instruments with detailed security descriptions and maturity information.",
      icon: <SafetyCertificateOutlined />,
      color: "#feca57",
      route: "/d/nsdl/4",
      features: ["Default Tracking", "Security Details", "Maturity Info"],
      stats: {
        total: "2",
        active: "2",
        volume: "₹15.5 Cr",
      },
    },
    {
      id: 5,
      title: "Securitised Instruments",
      subtitle: "Active Securitised Instruments",
      description:
        "Asset-backed securities and structured products with detailed underlying asset information and cash flow analysis.",
      icon: <GlobalOutlined />,
      color: "#48dbfb",
      route: "/d/nsdl/5",
      features: ["Asset backing", "Cash flow analysis", "Structure details"],
      stats: {
        total: "420+",
        active: "380+",
        volume: "₹22,500 Cr",
      },
    },
    {
      id: 6,
      title: "Certificates of Deposit",
      subtitle: "Active Certificates of Deposit",
      description:
        "Short-term negotiable instruments issued by banks and financial institutions. Explore detailed issuer information, maturity profiles, and interest rates.",
      icon: <CreditCardOutlined />,
      color: "#a084e8",
      route: "/d/nsdl/6",
      features: ["Bank/FI Issuers", "Maturity Profiles", "Interest Rates"],
      stats: {
        total: "1,100+",
        active: "950+",
        volume: "₹9,800 Cr",
      },
    },
  ];

  const stats = [
    {
      number: "41,487+",
      label: "Total Instruments",
      icon: <DatabaseOutlined />,
    },
    {
      number: "₹3,00,815 Cr",
      label: "Total Volume",
      icon: <BarChartOutlined />,
    },
    { number: "99.9%", label: "Data Accuracy", icon: <SafetyOutlined /> },
    { number: "Real-time", label: "Updates", icon: <EyeOutlined /> },
  ];

  return (
    <div className={`nsdl-container ${isDark ? "dark" : ""}`}>
      {/* Hero Section */}
      <div
        className="nsdl-hero"
        style={{
          background: 'linear-gradient(90deg, #3AA015 0%, #F49A24 50%, rgba(255, 0, 4, 0.61) 100%)',
          borderRadius: 18,
          padding: '2px',
          marginBottom: 32,
          position: 'relative',
        }}
      >
        <div
          style={{
            background: 'transparent',
            borderRadius: 16,
            padding: "48px 48px 40px 48px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            position: 'relative',
            overflow: 'hidden',
            minHeight: 180,
            width: '100%',
            height: '100%',
          }}
        >
          {/* Overlay for better text readability */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark 
                ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
              borderRadius: 16,
            }}
          />

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1
                style={{
                  margin: 0,
                  marginBottom: 12,
                  fontWeight: 800,
                  fontSize: 42,
                  color: isDark ? "#ffffff" : "#1a1a1a",
                }}
              >
                NSDL Data
              </h1>
              <p
                style={{
                  fontSize: 20,
                  color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
                  fontWeight: 400,
                  lineHeight: 1.4,
                  margin: 0,
                }}
              >
                Access comprehensive data from India's premier securities depository with real-time updates and detailed analytics
              </p>
            </div>

            {/* Stats on the right side - 2 per row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-end' }}>
              {/* First row - 2 stats */}
              <div style={{ display: 'flex', gap: '12px' }}>
                {stats.slice(0, 2).map((stat, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                    padding: '12px 16px',
                    borderRadius: 12,
                    boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    minWidth: '180px'
                  }}>
                    <div
                      style={{
                        color: '#667eea',
                        fontSize: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 8,
                        boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {stat.icon}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: isDark ? "#ffffff" : "#1a1a1a",
                        lineHeight: 1
                      }}>
                        {stat.number}
                      </span>
                      <span style={{
                        fontSize: 12,
                        color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666",
                        fontWeight: 500
                      }}>
                        {stat.label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Second row - 2 stats */}
              <div style={{ display: 'flex', gap: '12px' }}>
                {stats.slice(2, 4).map((stat, index) => (
                  <div key={index + 2} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                    padding: '12px 16px',
                    borderRadius: 12,
                    boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    minWidth: '180px'
                  }}>
                    <div
                      style={{
                        color: '#667eea',
                        fontSize: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 8,
                        boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {stat.icon}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: isDark ? "#ffffff" : "#1a1a1a",
                        lineHeight: 1
                      }}>
                        {stat.number}
                      </span>
                      <span style={{
                        fontSize: 12,
                        color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666",
                        fontWeight: 500
                      }}>
                        {stat.label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="nsdl-section">


        <div className="nsdl-services-grid">
          {nsdlServices.map((service) => (
            <div key={service.id} className="nsdl-service-card">
              <div className="nsdl-service-header">
                <div
                  className="nsdl-service-icon"
                  style={{ backgroundColor: service.color }}
                >
                  {service.icon}
                </div>
                <div className="nsdl-service-title">
                  <h3>{service.title}</h3>
                  <span className="nsdl-service-subtitle">
                    {service.subtitle}
                  </span>
                </div>
              </div>

              <p className="nsdl-service-description">{service.description}</p>

              <div className="nsdl-service-features">
                {service.features.map((feature, index) => (
                  <div key={index} className="nsdl-feature-tag">
                    {feature}
                  </div>
                ))}
              </div>

              <div className="nsdl-service-stats">
                <div className="nsdl-stat-item">
                  <span className="nsdl-stat-label">Total</span>
                  <span className="nsdl-stat-value">{service.stats.total}</span>
                </div>
                <div className="nsdl-stat-item">
                  <span className="nsdl-stat-label">Active</span>
                  <span className="nsdl-stat-value">
                    {service.stats.active}
                  </span>
                </div>
                <div className="nsdl-stat-item">
                  <span className="nsdl-stat-label">Volume</span>
                  <span className="nsdl-stat-value">
                    {service.stats.volume}
                  </span>
                </div>
              </div>

              <button
                className="nsdl-service-button"
                onClick={() => history(service.route)}
                style={{ backgroundColor: service.color }}
              >
                <EyeOutlined />
                View Data
              </button>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default NSDLHome;
